import { Dropdown } from "@carbon/react";
import { Controller, UseFormReturn, useWatch } from "react-hook-form";

import { useCreateError } from "src/errors";
import { useGetRuleOperatorsQuery } from "src/graphql/anti-fraud/queries/rules/RuleOperatorsQuery";
import { useGetOperatorLabel } from "src/hooks/useGetOperatorLabel";
import { mapToApiDataType } from "src/mappers/typedValue.mappers";

import { SimleRuleFormData } from "./useSimpleRuleDefinition";

export type RuleOperatorFormFieldProps = {
  index: number;
  id: string;
  methods: UseFormReturn<SimleRuleFormData>;
};

export function SimpleRuleOperatorsFormField({ index, id, methods }: RuleOperatorFormFieldProps) {
  const createError = useCreateError();
  const { control, setError } = methods;
  const dataType = useWatch({ control, name: "criteria" })[index].property?.dataType;
  const apiDataType = dataType ? mapToApiDataType(dataType) : undefined;

  const getOperatorLabel = useGetOperatorLabel();

  const { data } = useGetRuleOperatorsQuery({
    variables: { dataType: apiDataType ?? "String" },
    skip: !apiDataType,
    onError: () => {
      const error = createError("FETCH-OPERATORS-ERROR", null);
      setError("criteria", { message: error.message });
    },
  });
  const operators = data.filter(
    (operator) => operator.operatorValueType === "argument" || operator.operatorValueType === "none"
  );

  return (
    <Controller
      name={`criteria.${index}.operator`}
      control={control}
      render={({ field, fieldState }) => {
        return (
          <Dropdown
            id={`operator-${id}`}
            titleText=""
            label=""
            items={operators}
            itemToString={(item) => (item ? getOperatorLabel(item.code) : "")}
            selectedItem={operators.find((o) => o.code === field.value?.code) || null}
            onChange={({ selectedItem }) => {
              field.onChange({
                code: selectedItem?.code,
                valueType: selectedItem?.operatorValueType,
              });
            }}
            invalid={!!fieldState.error}
            invalidText={fieldState.error?.message}
            warn={false}
            disabled={!operators || operators.length === 0}
          />
        );
      }}
    />
  );
}
